import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { forkJoin } from "rxjs";
import { map, mergeMap } from "rxjs/operators";
import { move } from "../helpers/util";
import { ILearningObjectiveFilters } from "../model/interfaces";
import {
  DomainFilter,
  DomainFilterSpelling,
  LearningObjective,
  LearningObjectiveOrder,
  LearningObjectiveVM,
  SelectedLearningObjective,
} from "../model/model";
import { LearningObjectiveOrderService } from "../services/learning-objective-order.service";
import { LearningObjectiveService } from "../services/learning-objective.service";
import { LocalstorageService } from "../services/localstorage-service.service";
import { SchoolService } from "../services/school.service";
import { UserService } from "../services/user.service";

export class LoadLearningObjectiveAction {
  static type = "[Learning objectives] load";
  constructor() {}
}
export class UpdateSubjectCheckboxAction {
  static type = "[Learning objectives] filtered subjects";
  constructor(public checkbox: CheckboxModel) {}
}
export class UpdateAllSubjectCheckboxAction {
  static type = "[Learning objectives] filtered all subjects";
  constructor() {}
}
export class FilterByLoFilterAction {
  static type = "[Learning objectives] filtered by learningobjective radio";
  constructor(public filter: number) {}
}
export class FilterBySearchTextAction {
  static type = "[Learning objectives] filtered by searchtext";
  constructor(public searchtext: string) {}
}
export class ClearFiltersAction {
  static type = "[Learning objectives] clear filters";
  constructor() {}
}
export class SelectLearningobjectiveAction {
  static type = "[Learning objectives] select a learning objective";
  constructor(public learningObjective: LearningObjective) {}
}
export class DeselectLearningobjectiveAction {
  static type = "[Learning objectives] deselect a learning objective";
}
export class SetSelectedLearningObjectiveOrderAction {
  static type = "[Learning objectives] select a learning objective order";
  constructor(public learningObjectiveOrderId: string) {}
}

export class AddLearningObjectiveAction {
  static type = "[Learning objectives] Add learning objective to list";
  constructor(
    public learningObjectiveId: string,
    public cursorIndex: number,
  ) {}
}

export class RemoveLearningObjectiveAction {
  static type = "[Learning objectives] Remove learning objective from list";
  constructor(public learningObjectiveId: string) {}
}

export class UpdateSelectedLearningObjectiveOrderAction {
  static type = "[Learning objectives] Update select learning Objective order";
  constructor(
    public id: string,
    public newIndex: number,
  ) {}
}

export class UpdateSelectedLearningObjectiveOrderTitleAction {
  static type =
    "[Learning objectives] Update title select learning Objective order";
  constructor(
    public id: string,
    public title: string,
  ) {}
}

export class SetGradeFiltersAction {
  static type = "[Learning objectives] Set gradefilters";
  constructor(public filters: CheckboxModel[]) {}
}

export class SetDomainFiltersAction {
  static type = "[Learning objectives] Set domainfilters";
  constructor(public filters: CheckboxModel[]) {}
}

export class SetTagFiltersAction {
  static type = "[Learning objectives] Set tagfilters";
  constructor(public filters: CheckboxModel[]) {}
}

export class SetReferencelevelFiltersAction {
  static type = "[Learning objectives] Set referencefilters";
  constructor(public filters: CheckboxModel[]) {}
}

export class SetLearningrouteFiltersAction {
  static type = "[Learning objectives] Set learningroutefilters";
  constructor(public filters: CheckboxModel[]) {}
}

export class UpdateReferencelevelFiltersAction {
  static type = "[Learning objectives] filtered by referencelevel";
  constructor(public filter: { id: number; value: boolean }) {}
}

export class UpdateLearningrouteFiltersAction {
  static type = "[Learning objectives] filtered by learningroute";
  constructor(public filter: { id: number; value: boolean }) {}
}

export class UpdateGradeFiltersAction {
  static type = "[Learning objectives] update gradefilters";
  constructor(public filter: { id: number; value: boolean }) {}
}

export class UpdateDomainFiltersAction {
  static type = "[Learning objectives] update domainfilters";
  constructor(public filter: { id: number; value: boolean }) {}
}

export class UpdateTagFiltersAction {
  static type = "[Learning objectives] update tagfilters";
  constructor(public filter: { id: number; value: boolean }) {}
}

export class CheckUncheckAllGradeFiltersAction {
  static type = "[Learning objectives] update all gradefilters";
  constructor(public filter: boolean) {}
}

export class CheckUncheckAllDomainFiltersAction {
  static type = "[Learning objectives] update all domainfilters";
  constructor(public filter: boolean) {}
}

export class CheckUncheckAllTagFiltersAction {
  static type = "[Learning objectives] update all tagfilters";
  constructor(public filter: boolean) {}
}

export const initialState = {
  isLoading: true,
  hasLists: false,
  filters: {
    lofilter: 1,
    referencelevelfilter: [],
    learningroutefilter: [],
    gradelevelfilter: [],
    domainfilter: [],
    tagfilter: [],
    searchfilter: "",
    showRelatedOnly: false,
  },
  selectedLearningObjective: null,
  learningObjectives: [],
  selectedLearningObjectiveOrder: null,
  subjectsInLearningObjectives: null,
  subjectsInList: null,
};

export interface LearningObjectiveData {
  allLearningObjectives: LearningObjectiveVM[];
  learningObjectivesInList: LearningObjectiveVM[];
  selectedLearningObjectiveOrder: LearningObjectiveOrder;
  selectedLearningObjective: LearningObjective;
  hasLists: boolean;
  isLoading: boolean;
  subjectsInLearningObjectives: Array<CheckboxModel>;
  subjectsInList: Array<CheckboxModel>;
  visibleSubjects?: Array<CheckboxModel>;
}

export interface LearningObjectivesStateModel {
  isLoading: boolean;
  hasLists: boolean;
  filters: ILearningObjectiveFilters;
  selectedLearningObjective: LearningObjective;
  learningObjectives: LearningObjectiveVM[];
  selectedLearningObjectiveOrder: LearningObjectiveOrder;
  subjectsInLearningObjectives: Array<CheckboxModel>;
  subjectsInList: Array<CheckboxModel>;
}

export interface CheckboxModel {
  id: number;
  value: number | string;
  name: string;
  shortName?: string;
  checked: boolean;
}

@State<LearningObjectivesStateModel>({
  name: "learningObjectives",
  defaults: initialState,
})
@Injectable()
export class LearningObjectivesState {
  constructor(
    private learningObjectiveOrderService: LearningObjectiveOrderService,
    private learningObjectiveService: LearningObjectiveService,
    private userService: UserService,
    private schoolService: SchoolService,
    private storageService: LocalstorageService,
  ) {}

  @Selector()
  static filters(state: LearningObjectivesStateModel) {
    return state.filters;
  }

  @Selector()
  static isLoading(state: LearningObjectivesStateModel) {
    return state.isLoading;
  }

  @Selector()
  static allLearningObjectives(state: LearningObjectivesStateModel) {
    return state.learningObjectives;
  }

  @Selector()
  static subjectsInLearningObjectives(state: LearningObjectivesStateModel) {
    return state.subjectsInLearningObjectives;
  }

  @Selector()
  static selectedLearningObjective(
    state: LearningObjectivesStateModel,
  ): SelectedLearningObjective {
    return state.selectedLearningObjective
      ? { ref: state.selectedLearningObjective }
      : { ref: null };
  }

  @Selector()
  static selectedLearningObjectiveOrder(
    state: LearningObjectivesStateModel,
  ): LearningObjectiveOrder {
    return state.selectedLearningObjectiveOrder;
  }

  @Selector()
  static subjectsInList(
    state: LearningObjectivesStateModel,
  ): LearningObjectiveData["subjectsInList"] {
    return state.subjectsInList;
  }

  @Selector()
  static learningObjectives(
    state: LearningObjectivesStateModel,
  ): LearningObjectiveData {
    const f = state.filters;
    const los: LearningObjectiveVM[] = (() => {
      if (!state.selectedLearningObjectiveOrder?.learningObjectives?.length) {
        return [];
      }

      const loMap = new Map(
        state.learningObjectives?.map((l) => [l.id, l]) ?? [],
      );

      return state.selectedLearningObjectiveOrder.learningObjectives
        .map((id) => loMap.get(id))
        .filter((lo): lo is LearningObjectiveVM => !!lo)
        .sort((a, b) => {
          const domainCompare = a.domainTitle.localeCompare(b.domainTitle);
          if (domainCompare !== 0) return domainCompare;
          return LearningObjectivesState.compareSubjectCodes(
            a.subjectCode,
            b.subjectCode,
          );
        });
    })();

    const losInList = los.filter((l) =>
      l
        ? (f.domainfilter
            ? LearningObjectivesState.isDomainInFilter(l, f.domainfilter)
            : true) &&
          (f.gradelevelfilter
            ? LearningObjectivesState.isGroupInFilter(
                l.gradeLevel,
                f.gradelevelfilter,
              )
            : true) &&
          (f.lofilter === 3 && f.tagfilter
            ? LearningObjectivesState.isTagInFilter(l, f)
            : true) &&
          (f.lofilter === 1
            ? LearningObjectivesState.isRefLevelInFilter(
                l.learningroutes,
                f.referencelevelfilter,
              )
            : true) &&
          (f.lofilter === 2
            ? LearningObjectivesState.isLearningrouteInFilter(
                l.learningroutes,
                f.learningroutefilter,
              )
            : true) &&
          (f.searchfilter !== ""
            ? l.title.indexOf(f.searchfilter.toString()) > -1
            : true)
        : false,
    );

    const subjectsInList =
      state.subjectsInLearningObjectives?.filter(
        (s) =>
          s.name ===
          losInList.map((l) => l.subjectCode).find((code) => code === s.name),
      ) || [];

    return {
      learningObjectivesInList:
        losInList.filter((l) =>
          l
            ? LearningObjectivesState.isSubjectInFilter(l, state.subjectsInList)
            : true,
        ) || [],
      allLearningObjectives: state.learningObjectives.map((lo) => ({
        ...lo,
        inList:
          state.selectedLearningObjectiveOrder?.learningObjectives?.indexOf(
            lo.id,
          ) > -1
            ? true
            : false,
      })),
      selectedLearningObjectiveOrder: state.selectedLearningObjectiveOrder,
      selectedLearningObjective: state.selectedLearningObjective,
      hasLists: state.hasLists,
      isLoading: state.isLoading,
      subjectsInLearningObjectives: state.subjectsInLearningObjectives,
      subjectsInList: subjectsInList,
      visibleSubjects: subjectsInList,
    };
  }

  private static isRefLevelInFilter(
    referenceLevels: string[],
    referenceLevelFilter: CheckboxModel[],
  ): boolean {
    if (
      referenceLevels.length === 0 ||
      referenceLevels.findIndex((ref) => ref === "") > -1
    )
      return true;

    const refLevelIsInFilter = referenceLevelFilter.find(
      (g) =>
        g.checked && g.name === referenceLevels.find((ref) => ref === g.value),
    );
    return refLevelIsInFilter ? true : false;
  }

  private static isLearningrouteInFilter(
    learningroutes: string[],
    learningrouteFilter: CheckboxModel[],
  ): boolean {
    if (
      learningroutes.length === 0 ||
      learningroutes.findIndex((ref) => ref === "") > -1
    )
      return true;
    const learningrouteIsInFilter = learningrouteFilter.find(
      (g) =>
        g.checked && g.name === learningroutes.find((ref) => ref === g.value),
    );
    return learningrouteIsInFilter ? true : false;
  }

  private static isGroupInFilter(
    learningObjectiveGradeLevel: number,
    gradeLevelFilter: CheckboxModel[],
  ): boolean {
    if (!learningObjectiveGradeLevel) return true;
    const gradeLevelIsInFilter = gradeLevelFilter.find(
      (g) => g.checked && g.value === learningObjectiveGradeLevel,
    );
    return gradeLevelIsInFilter ? true : false;
  }

  private static isDomainInFilter(
    learningObjective: LearningObjective,
    domainFilter: CheckboxModel[],
  ): boolean {
    const domainTitle = learningObjective.domainTitle.toUpperCase();
    const charactersAndSpacesReplaced = domainTitle.replace(
      /[^A-Z0-9]+/gi,
      "_",
    );

    return domainFilter.find(
      (d) =>
        d.checked &&
        (DomainFilter[d.value] === charactersAndSpacesReplaced ||
          DomainFilterSpelling[d.value] === charactersAndSpacesReplaced),
    )
      ? true
      : false;
  }

  private static isSubjectInFilter(
    learningObjective: LearningObjective,
    subjects: CheckboxModel[],
  ): boolean {
    const subjectCode = learningObjective.subjectCode;
    if (subjects.find((s) => s.name === subjectCode && s.checked === true)) {
      return true;
    } else {
      return false;
    }
  }

  private static isTagInFilter(
    learningObjective: LearningObjective,
    learningObjectiveFilter: ILearningObjectiveFilters,
  ): boolean {
    // uncomment the underlaying variables to assign random tags to learningobjectives
    // in order to test the filtersystem in case the taglis property is empty for the resultset
    // const tagArray = ['M3', 'E3', 'M4', 'E4', 'M5', 'E5', 'M6', 'E6', 'M7', 'E7', 'B8', 'M8', 'E8']
    // learningObjective.tagList[0] = tagArray[Math.floor(Math.random() * tagArray.length)];
    // learningObjective.tagList[1] = tagArray[Math.floor(Math.random() * tagArray.length)];

    if (learningObjective.tagList.length) {
      for (let i = 0; i < learningObjective.tagList.length; i++) {
        const tag = learningObjective.tagList[i];
        if (
          learningObjectiveFilter.tagfilter.findIndex(
            (t) => t.checked && t.name === tag,
          ) > -1
        ) {
          return true;
        }
      }
      return false;
    } else {
      return false;
    }
  }

  @Selector()
  static allGradeLevelsSelected(state: LearningObjectivesStateModel): boolean {
    return state.filters.gradelevelfilter.every(
      (item: CheckboxModel) => item.checked === true,
    );
  }

  @Selector()
  static allTagsSelected(state: LearningObjectivesStateModel): boolean {
    return state.filters.tagfilter.every(
      (item: CheckboxModel) => item.checked === true,
    );
  }

  @Selector()
  static allDomainsSelected(state: LearningObjectivesStateModel): boolean {
    return state.filters.domainfilter.every(
      (item: CheckboxModel) => item.checked === true,
    );
  }

  @Selector()
  static refLevelPlaceholder(state: LearningObjectivesStateModel): string {
    return state.filters.lofilter === 1
      ? (state.filters.referencelevelfilter.find((f) => f.checked)?.name ??
          "Inactief")
      : "Inactief";
  }

  @Selector()
  static learningRoutePlaceholder(state: LearningObjectivesStateModel): string {
    return state.filters.lofilter === 2
      ? (state.filters.learningroutefilter.find((f) => f.checked)?.name ??
          "Inactief")
      : "Inactief";
  }

  @Selector()
  static lvsPlaceholder(state: LearningObjectivesStateModel): string {
    if (state.filters.lofilter === 3) {
      const checkedfilters = state.filters.tagfilter.filter((f) => f.checked);
      if (
        state.filters.tagfilter.every(
          (item: CheckboxModel) => item.checked === true,
        )
      )
        return "Alle";
      if (checkedfilters.length > 5)
        return `${checkedfilters.length} filters actief`;
      if (checkedfilters.length === 0) return "Geen filters actief";
      else return checkedfilters.map((filter) => filter.name).join(", ");
    } else {
      return "Inactief";
    }
  }

  @Selector()
  static yearGroupPlaceholder(state: LearningObjectivesStateModel): string {
    const checkedfilters = state.filters.gradelevelfilter.filter(
      (f) => f.checked,
    );
    if (
      state.filters.gradelevelfilter.every(
        (item: CheckboxModel) => item.checked === true,
      )
    )
      return "Alle";
    if (checkedfilters.length > 5)
      return `${checkedfilters.length} filters actief`;
    if (checkedfilters.length === 0) return "Geen filters actief";
    else return checkedfilters.map((filter) => filter.value).join(", ");
  }

  @Selector()
  static domainPlaceholder(state: LearningObjectivesStateModel): string {
    const checkedfilters = state.filters.domainfilter.filter((f) => f.checked);
    if (
      state.filters.domainfilter.every(
        (item: CheckboxModel) => item.checked === true,
      )
    )
      return "Alle";
    if (checkedfilters.length > 5)
      return `${checkedfilters.length} filters actief`;
    if (checkedfilters.length === 0) return "Geen filters actief";
    else return checkedfilters.map((filter) => filter.shortName).join(", ");
  }

  @Action(LoadLearningObjectiveAction)
  loading(ctx: StateContext<LearningObjectivesStateModel>) {
    const state = ctx.getState();

    const previousSessionData = this.storageService.get(
      `previousSession-${this.userService.user?.id}`,
    );
    let groupId;
    previousSessionData &&
    previousSessionData.groupId &&
    this.userService.user?.groupList.find(
      (g: string) => g === previousSessionData.groupId,
    )
      ? (groupId = previousSessionData.groupId)
      : (groupId = this.userService.user?.groupList[0]);

    return forkJoin(
      this.learningObjectiveService.getAll(),
      this.learningObjectiveOrderService.getByGroup(groupId),
      this.schoolService.getLearningObjectiveOrder(),
    ).pipe(
      map(([los, allLists, templatelists]) => {
        const template = templatelists.find(
          (t) => t.isTemplate && t.learningObjectives.length > 0,
        );
        const subjects = los
          .map((l) => l.subjectCode)
          .filter((value, index, self) => self.indexOf(value) === index)
          .sort((a, b) => LearningObjectivesState.compareSubjectCodes(a, b))
          .map((subject, index) => {
            return {
              id: index,
              value: index,
              name: subject,
              checked: true,
            };
          });
        ctx.patchState({
          learningObjectives: los.map((l) => ({ ...l, inList: false })),
          selectedLearningObjectiveOrder: template,
          isLoading: false,
          hasLists: (allLists && allLists.length > 0) || template != undefined,
          subjectsInLearningObjectives: subjects,
          subjectsInList: subjects,
        });

        // if (template) {
        //   return ctx.dispatch(new SetSelectedLearningObjectiveOrderAction(template ? template.id : allLists[0].id));
        // }
      }),
    );
  }

  @Action(SetGradeFiltersAction)
  setGradeFilters(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: SetGradeFiltersAction,
  ) {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        gradelevelfilter: action.filters,
      },
    });
  }

  @Action(UpdateGradeFiltersAction)
  updateGradeFilters(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: UpdateGradeFiltersAction,
  ) {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        gradelevelfilter: state.filters.gradelevelfilter.map(
          (gradefilter: CheckboxModel) => {
            if (gradefilter.id === action.filter.id) {
              return {
                ...gradefilter,
                checked: action.filter.value,
              };
            }
            return gradefilter;
          },
        ),
      },
    });
  }

  @Action(CheckUncheckAllGradeFiltersAction)
  checkUncheckGradeFilters(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: CheckUncheckAllGradeFiltersAction,
  ) {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        gradelevelfilter: state.filters.gradelevelfilter.map(
          (gradefilter: CheckboxModel) => {
            return {
              ...gradefilter,
              checked: action.filter,
            };
          },
        ),
      },
    });
  }

  @Action(SetDomainFiltersAction)
  setDomainFilters(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: SetDomainFiltersAction,
  ) {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        domainfilter: action.filters,
      },
    });
  }

  @Action(UpdateDomainFiltersAction)
  updateDomainFilters(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: UpdateDomainFiltersAction,
  ) {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        domainfilter: state.filters.domainfilter.map(
          (domain: CheckboxModel) => {
            if (domain.id === action.filter.id) {
              return {
                ...domain,
                checked: action.filter.value,
              };
            }
            return domain;
          },
        ),
      },
      // Reset all subject filters when domain changes
      subjectsInList: state.subjectsInList.map((subject: CheckboxModel) => ({
        ...subject,
        checked: true,
      })),
    });
  }

  @Action(CheckUncheckAllDomainFiltersAction)
  checkUncheckDomainFilters(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: CheckUncheckAllDomainFiltersAction,
  ) {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        domainfilter: state.filters.domainfilter.map(
          (domain: CheckboxModel) => {
            return {
              ...domain,
              checked: action.filter,
            };
          },
        ),
      },
    });
  }

  @Action(SetTagFiltersAction)
  setTagFilters(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: SetTagFiltersAction,
  ) {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        tagfilter: action.filters,
      },
    });
  }

  @Action(SetReferencelevelFiltersAction)
  setReferencelevelFilters(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: SetReferencelevelFiltersAction,
  ) {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        referencelevelfilter: action.filters,
      },
    });
  }

  @Action(SetLearningrouteFiltersAction)
  setLearningrouteFilters(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: SetLearningrouteFiltersAction,
  ) {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        learningroutefilter: action.filters,
      },
    });
  }

  @Action(UpdateReferencelevelFiltersAction)
  filterByReferenceLevel(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: UpdateReferencelevelFiltersAction,
  ) {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        learningroutefilter: state.filters.learningroutefilter.map(
          (ref: CheckboxModel) => ({ ...ref, checked: false }),
        ),
        referencelevelfilter: state.filters.referencelevelfilter.map(
          (ref: CheckboxModel) => {
            if (ref.id === action.filter.id) {
              return {
                ...ref,
                checked: action.filter.value,
              };
            } else {
              return {
                ...ref,
                checked: false,
              };
            }
          },
        ),
      },
    });
  }

  @Action(UpdateLearningrouteFiltersAction)
  filterByLearningroute(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: UpdateLearningrouteFiltersAction,
  ) {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        referencelevelfilter: state.filters.referencelevelfilter.map(
          (ref: CheckboxModel) => ({ ...ref, checked: false }),
        ),
        learningroutefilter: state.filters.learningroutefilter.map(
          (ref: CheckboxModel) => {
            if (ref.id === action.filter.id) {
              return {
                ...ref,
                checked: action.filter.value,
              };
            } else {
              return {
                ...ref,
                checked: false,
              };
            }
          },
        ),
      },
    });
  }

  @Action(UpdateTagFiltersAction)
  updateTagFilters(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: UpdateTagFiltersAction,
  ) {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        tagfilter: state.filters.tagfilter.map((tag: CheckboxModel) => {
          if (tag.id === action.filter.id) {
            return {
              ...tag,
              checked: action.filter.value,
            };
          }
          return tag;
        }),
      },
    });
  }

  @Action(CheckUncheckAllTagFiltersAction)
  checkUncheckTagFilters(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: CheckUncheckAllTagFiltersAction,
  ) {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        tagfilter: state.filters.tagfilter.map((tag: CheckboxModel) => {
          return {
            ...tag,
            checked: action.filter,
          };
        }),
      },
    });
  }

  @Action(ClearFiltersAction)
  clearFiltersAction(ctx: StateContext<LearningObjectivesStateModel>) {
    ctx.patchState({ filters: initialState.filters });
  }

  @Action(UpdateSelectedLearningObjectiveOrderAction)
  updateSelectedLearningObjectiveOrderAction(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: UpdateSelectedLearningObjectiveOrderAction,
  ) {
    const state = ctx.getState();
    const selected = state.selectedLearningObjectiveOrder;
    const oldIndex = selected.learningObjectives.indexOf(action.id);
    if (oldIndex !== action.newIndex) {
      move(selected.learningObjectives, oldIndex, action.newIndex);
      ctx.patchState({
        selectedLearningObjectiveOrder: selected,
      });
      return this.learningObjectiveOrderService.update(
        selected.id,
        selected.learningObjectives,
      );
    }
  }

  @Action(UpdateSelectedLearningObjectiveOrderTitleAction)
  updateSelectedLearningObjectiveOrderTitleAction(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: UpdateSelectedLearningObjectiveOrderTitleAction,
  ) {
    const state = ctx.getState();

    ctx.patchState({
      selectedLearningObjectiveOrder: {
        ...state.selectedLearningObjectiveOrder,
        title: action.title,
      },
    });
    return this.learningObjectiveOrderService.updateTitle(
      action.id,
      action.title,
    );
  }

  @Action(SetSelectedLearningObjectiveOrderAction)
  setSelected(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: SetSelectedLearningObjectiveOrderAction,
  ) {
    return this.userService
      .setSelectedList(action.learningObjectiveOrderId)
      .pipe(
        mergeMap((_) =>
          this.learningObjectiveOrderService
            .get(action.learningObjectiveOrderId)
            .pipe(
              map((lo) =>
                ctx.patchState({
                  selectedLearningObjectiveOrder: lo,
                  hasLists: !!lo,
                }),
              ),
            ),
        ),
      );
  }

  @Action(SelectLearningobjectiveAction)
  selectLearningObjective(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: SelectLearningobjectiveAction,
  ) {
    ctx.patchState({
      selectedLearningObjective: action.learningObjective,
    });
  }

  @Action(DeselectLearningobjectiveAction)
  deselectLearningObjective(ctx: StateContext<LearningObjectivesStateModel>) {
    ctx.patchState({
      selectedLearningObjective: null,
    });
  }

  @Action(UpdateSubjectCheckboxAction)
  updateSubjectCheckbox(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: UpdateSubjectCheckboxAction,
  ) {
    const state = ctx.getState();

    const newCheckedState = !action.checkbox.checked;

    ctx.patchState({
      subjectsInList: state.subjectsInList.map((c) => {
        const isChecked =
          c.id === action.checkbox.id ? newCheckedState : c.checked;

        return {
          ...c,
          checked: isChecked,
        };
      }),
    });
  }

  @Action(UpdateAllSubjectCheckboxAction)
  updateAllSubjectCheckbox(ctx: StateContext<LearningObjectivesStateModel>) {
    const state = ctx.getState();
    const allChecked = state.subjectsInList.every(
      (c: CheckboxModel) => c.checked === true,
    );

    const newCheckedState = allChecked ? false : true;

    ctx.patchState({
      subjectsInList: state.subjectsInList.map((c: CheckboxModel) => {
        return {
          ...c,
          checked: newCheckedState,
        };
      }),
    });
  }

  @Action(FilterByLoFilterAction)
  filterByLo(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: FilterByLoFilterAction,
  ) {
    const state = ctx.getState();
    const refFiltersDefault = state.filters.referencelevelfilter.map(
      (filter, index) => ({ ...filter, checked: index === 0 ? true : false }),
    );
    const learningrouteFiltersDefault = state.filters.learningroutefilter.map(
      (filter, index) => ({ ...filter, checked: index === 0 ? true : false }),
    );
    const allRefFiltersUnChecked = state.filters.referencelevelfilter.every(
      (f) => !f.checked,
    );
    const allLearningrouteFiltersUnChecked =
      state.filters.learningroutefilter.every((f) => !f.checked);
    ctx.patchState({
      filters: {
        ...state.filters,
        lofilter: action.filter,
        referencelevelfilter:
          action.filter === 1 && allRefFiltersUnChecked
            ? refFiltersDefault
            : state.filters.referencelevelfilter,
        learningroutefilter:
          action.filter === 2 && allLearningrouteFiltersUnChecked
            ? learningrouteFiltersDefault
            : state.filters.learningroutefilter,
      },
    });
  }

  @Action(FilterBySearchTextAction)
  filterBySearchText(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: FilterBySearchTextAction,
  ) {
    const state = ctx.getState();
    ctx.patchState({
      filters: {
        ...state.filters,
        searchfilter: action.searchtext,
        showRelatedOnly: false,
      },
    });
  }

  @Action(AddLearningObjectiveAction)
  addLearningObjective(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: AddLearningObjectiveAction,
  ) {
    const state = ctx.getState();
    const list = state.selectedLearningObjectiveOrder;

    const newIndex = action.cursorIndex;

    ctx.patchState({
      selectedLearningObjectiveOrder: {
        ...list,
        learningObjectives: this.addAtIndex(
          list.learningObjectives,
          action.learningObjectiveId,
          newIndex >= 0
            ? newIndex
            : list.learningObjectives
              ? list.learningObjectives.length
              : 0,
        ),
      },
    });
    return this.learningObjectiveOrderService.update(
      state.selectedLearningObjectiveOrder.id,
      state.selectedLearningObjectiveOrder.learningObjectives,
    );
  }

  @Action(RemoveLearningObjectiveAction)
  removeLearningObjective(
    ctx: StateContext<LearningObjectivesStateModel>,
    action: RemoveLearningObjectiveAction,
  ) {
    const state = ctx.getState();
    const learningObjectives =
      state.selectedLearningObjectiveOrder.learningObjectives.filter(
        (lo) => lo !== action.learningObjectiveId,
      );
    ctx.patchState({
      selectedLearningObjectiveOrder: {
        ...state.selectedLearningObjectiveOrder,
        learningObjectives: learningObjectives,
      },
    });
    return this.learningObjectiveOrderService.update(
      state.selectedLearningObjectiveOrder.id,
      learningObjectives,
    );
  }

  private static compareSubjectCodes(aCode: string, bCode: string): number {
    const aFirst = aCode.split("-")[0];
    const bFirst = bCode.split("-")[0];

    // Split by dot to separate number from letter suffix
    const aParts = aFirst.split(".");
    const bParts = bFirst.split(".");

    const aNum = parseInt(aParts[0], 10) || 0;
    const bNum = parseInt(bParts[0], 10) || 0;

    const aLetter = aParts[1] || "";
    const bLetter = bParts[1] || "";

    // Check for leading zeros (01-09)
    const aHasLeadingZero = aParts[0].startsWith("0");
    const bHasLeadingZero = bParts[0].startsWith("0");

    // Sort by category: leading zeros first (01-09), then double digits (10+), then single digits
    const aCategory = aHasLeadingZero ? 0 : aNum >= 10 ? 1 : 2;
    const bCategory = bHasLeadingZero ? 0 : bNum >= 10 ? 1 : 2;

    if (aCategory !== bCategory) return aCategory - bCategory;

    // Same category: sort by numeric value
    if (aNum !== bNum) return aNum - bNum;

    // Same number: no letter comes before letter
    if (!aLetter && bLetter) return -1;
    if (aLetter && !bLetter) return 1;

    // Both have letters: sort alphabetically by letter
    return aLetter.localeCompare(bLetter);
  }

  private addAtIndex<T>(arr: Array<T>, item, index): Array<T> {
    if (!arr) {
      arr = [];
    }
    arr.splice(index, 0, item);
    return arr;
  }
}
